<template>
  <div class="chat-window">
    <!-- Шапка чата -->
    <div class="chat-header">
      <div class="left-part">
        <img
          :src="interlocutorAvatar"
          class="avatar"
          alt="Avatar"
        />
        <div class="info">
          <h3 class="name">{{ interlocutor.name }}</h3>
          <div class="status-row">
            <p class="specifics">{{ interlocutor.specifics }}</p>
            <span
              class="status-dot"
              :class="{ online: interlocutorOnline, offline: !interlocutorOnline }"
            ></span>
            <span
              class="status-text"
              :class="{ typing: isInterlocutorTyping }"
            >
              {{ isInterlocutorTyping ? 'печатает...' : (interlocutorOnline ? 'Online' : 'Offline') }}
            </span>
          </div>
        </div>
      </div>

      <button
        class="call-button"
        :disabled="isCreatingConference || !chatId"
        :title="!chatId ? 'Сначала отправьте сообщение, чтобы создать чат' : 'Создать видеозвонок'"
        @click="createVideoConference"
      >
        <span v-if="isCreatingConference" class="call-spinner"></span>

        <template v-else>
          <img src="/call-icon.png" alt="Call" />
          <span>Звонок</span>
        </template>
      </button>
    </div>

    <div class="chat-separator"></div>

    <div class="messages-list" ref="messagesList">

      <template v-if="isLoadingMessages && chatId">

        <div class="skeleton-container">
          <div
            v-for="n in 6"
            :key="n"
            :class="['message-skeleton-wrapper', n % 2 === 0 ? 'own' : 'interlocutor']"
          >
            <div
              v-if="n % 2 !== 0"
              class="skeleton-avatar shimmer"
            ></div>

            <div
              class="message-skeleton-bubble shimmer"
              :style="{
                width: `${70 + n * 3}%`,  
                height: `${38 + n * 2}px`,
                borderTopLeftRadius: n % 2 === 0 ? '16px' : '4px',
                borderTopRightRadius: n % 2 === 0 ? '4px' : '16px',
                borderBottomLeftRadius: '16px',
                borderBottomRightRadius: '16px'
              }"
            >
              <div class="skeleton-line short shimmer"></div>
              <div class="skeleton-line shimmer"></div>
            </div>
          </div>
        </div>
      </template>

      <template v-else-if="isGhostChat && messages.length === 0">
        <div class="empty-chat-placeholder">
          <img src="../../assets/empty-ghost-chat.png" alt="Нет сообщений" class="empty-chat-icon" />
          <p class="empty-chat-text">Пока что здесь нет сообщений</p>
          <p class="empty-chat-subtext">Напишите первым, чтобы начать общение</p>
        </div>
      </template>

      <template v-else>
        <div
          v-for="message in messages"
          :key="message.id"
          :data-message-id="message.id"
          :ref="el => observeMessage(el, message)"
          :class="[
            'message-item',
            {
              'own-message': message.senderId === currentUserId,
              'sending-message': message.isSending,
              'failed-message': message.isFailed
            }
          ]"
        @mousedown="startPressTimer(message, $event)"
        @mousemove="handlePressMove"
        @mouseup="cancelPressTimer"
        @mouseleave="cancelPressTimer"
        @touchstart.passive="startPressTimer(message, $event.touches[0])"
        @touchmove.passive="handlePressMove($event.touches[0])"
        @touchend="cancelPressTimer"
        @contextmenu.prevent="openMessageContextMenu(message, $event)"
        >
          <div v-if="message.senderId !== currentUserId" class="avatar-container">
            <img
              :src="interlocutorAvatar"
              class="message-avatar"
              alt="Avatar"
            />
          </div>

          <div class="message-content">
            <div
              v-if="message.replyTo && !message.is_deleted"
              class="reply-quote"
              @click.stop="scrollToRepliedMessage(message.replyTo.messageId)"
            >
              <div class="reply-accent"></div>

              <div class="reply-quote-content">
                <div class="reply-author">
                  {{ replyAuthorName(message.replyTo) }}
                </div>

                <div class="reply-text">
                  {{ replyPreviewText(message.replyTo) }}
                </div>
              </div>
            </div>

            <p v-if="message.is_deleted" class="text deleted">
              <i>сообщение удалено</i>
            </p>

            <template v-else>

              <div
                v-if="message.messageType === 'video_call'"
                class="video-call-message"
                >
                <div class="video-call-icon">📹</div>
                <div class="video-call-info">
                  <div class="video-call-title">
                    {{ message.videoTitle || 'Видеозвонок' }}
                  </div>
                  <div class="video-call-provider">
                    Яндекс Телемост
                  </div>
                  <button
                    class="video-call-open-button"
                    :disabled="!message.videoJoinUrl"
                    @click.stop="openVideoConference(message.videoJoinUrl)"
                  >
                    Перейти в видеозвонок
                  </button>
                </div>
                </div>

                <div
                  v-else-if="message.imageUrl"
                  class="message-image-wrapper"
                  >
                <div
                  v-if="!loadedImages[message.id]"
                  class="image-shimmer"
                ></div>

                <img
                  :key="message.imageUrl"
                  :src="message.imageUrl"
                  class="message-image"
                  :class="{ loaded: loadedImages[message.id] }"
                  alt="image"
                  @load="loadedImages[message.id] = true"
                  @click.stop="openImage(message.imageUrl)"
                />
              </div>

              <div
                v-else-if="message.attachmentUrl"
                class="file-message"
                @click.stop="downloadFile(message)"
              >
                <div class="file-icon">
                  {{ fileIcon(message) }}
                </div>

                <div class="file-info">
                  <div class="file-name">
                    {{ message.attachmentName || 'Файл' }}
                  </div>
                  <div class="file-meta">
                    {{ downloadingFiles[message.id] ? 'Скачивание…' : fileMeta(message) }}
                  </div>
                </div>

                <div class="file-download">
                  <span v-if="downloadingFiles[message.id]" class="file-spinner"></span>
                  <span v-else>↓</span>
                </div>
              </div>

              <p v-else class="text">
                {{ message.textMessage }}
              </p>
            </template>
            <span class="time">
              {{ formatTime(message.sendDate) }}
              <template v-if="message.isRead">
                <img 
                  :src="message.senderId === currentUserId ? '/is-read.png' : '/is-read-interlocator.png'"
                  class="read-icon"
                />
              </template>
              <template v-else>
                <img 
                  :src="message.senderId === currentUserId ? '/is-unread.png' : '/is-unread-interlocator.png'"
                  class="read-icon"
                />
              </template>
            </span>
            <span v-if="message.isSending" class="sending-label">отправка…</span>
            <span v-else-if="message.isFailed" class="failed-label">ошибка</span>
            <button
              v-if="message.isFailed"
              class="retry-button"
              @click.stop="retryMessage(message)"
            >
              Повторить
            </button>
          </div>
        </div>
      </template>

      <transition name="fade">
        <div v-if="showDeleteMenu" class="delete-popup-overlay" @click.self="showDeleteMenu = false">
          <div class="delete-popup">
            <p>Удалить сообщение?</p>
            <button class="delete-button" @click="deleteMessage">Удалить</button>
            <button class="cancel-button" @click="showDeleteMenu = false">Отмена</button>
          </div>
        </div>
      </transition>

      <Teleport to="body">
        <transition name="message-menu">
          <div
            v-if="showMessageMenu"
            class="message-menu-overlay"
            @mousedown.self="closeMessageMenu"
            @touchstart.self="closeMessageMenu"
          >
            <div
              class="message-context-menu"
              :style="{
                top: `${messageMenuPosition.top}px`,
                left: `${messageMenuPosition.left}px`
              }"
            >
              <button
                v-if="canReplyToSelectedMessage"
                type="button"
                class="message-menu-action"
                @click="replyToSelectedMessage"
              >
                <span class="message-menu-icon">↩</span>
                <span>Ответить</span>
              </button>

              <button
                v-if="canCopySelectedMessage"
                type="button"
                class="message-menu-action"
                @click="copySelectedMessage"
              >
                <span class="message-menu-icon">⧉</span>
                <span>Скопировать</span>
              </button>

              <button
                v-if="selectedMessage?.id"
                type="button"
                class="message-menu-action destructive"
                @click="openDeleteConfirmation"
              >
                <span class="message-menu-icon">⌫</span>
                <span>Удалить</span>
              </button>
            </div>
          </div>
        </transition>
      </Teleport>

    </div>

    <transition name="reply-preview">
      <div v-if="repliedMessage" class="reply-input-preview">
        <div class="reply-input-accent"></div>

        <div class="reply-input-content">
          <div class="reply-input-author">
            {{ repliedMessage.senderId === currentUserId
              ? 'Вы'
              : interlocutor.name || repliedMessage.senderName || 'Пользователь'
            }}
          </div>

          <div class="reply-input-text">
            {{ messagePreviewText(repliedMessage) }}
          </div>
        </div>

        <button
          type="button"
          class="reply-input-close"
          aria-label="Закрыть ответ"
          @click="clearReply"
        >
          ×
        </button>
      </div>
    </transition>

    <div class="message-input">
      <button
        class="file-button"
        @click="openFilePicker"
      >
        <img src="/add-file.png" alt="Attach" />
      </button>

      <input
        v-model="newMessage"
        type="text"
        placeholder="Напишите сообщение"
        class="message-field"
        @input="handleTyping"
        @keyup.enter="sendMessage"
      />

      <button class="send-button" @click="sendMessage" :disabled="!newMessage.trim()">
        <img src="/send-message.png" alt="Send" />
      </button>
    </div>

    <input
      ref="fileInput"
      type="file"
      style="display:none"
      @change="handleFileSelected"
    />

    <div class="right-separator"></div>

  </div>
</template>
  
<script setup>

import { useRouter } from 'vue-router'
import { ref, computed, nextTick, watch, onMounted, onUnmounted } from 'vue';

import ChatsService from '../../api/ChatsService';
import VideoConferenceService from '../../api/VideoConferenceService';

import emptyAvatar from "../../assets/default-avatar.png";

import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  serverTimestamp,
  onSnapshot
} from "firebase/firestore";

let typingTimer = null;
let isTypingSent = false;
let unsubscribeTyping = null;

const selectedMessage = ref(null);
const repliedMessage = ref(null);

const showMessageMenu = ref(false);
const showDeleteMenu = ref(false);

const messageMenuPosition = ref({
  top: 0,
  left: 0
});

const interlocutorAvatar = computed(() => {
  const avatar =
    props.interlocutor?.avatar;

  return avatar == null || avatar === ""
    ? emptyAvatar
    : avatar;
});

const isCreatingConference = ref(false);
let pressTimer = null;
let pressStartPoint = null;

function startPressTimer(message, event) {
  cancelPressTimer();

  pressStartPoint = {
    x: event.clientX,
    y: event.clientY
  };

  pressTimer = window.setTimeout(() => {
    selectedMessage.value = message;

    const menuWidth = 220;
    const menuHeight = 150;
    const padding = 12;

    const left = Math.min(
      Math.max(event.clientX - menuWidth / 2, padding),
      window.innerWidth - menuWidth - padding
    );

    const top = Math.min(
      Math.max(event.clientY - 20, padding),
      window.innerHeight - menuHeight - padding
    );

    messageMenuPosition.value = {
      top,
      left
    };

    showMessageMenu.value = true;

    if (navigator.vibrate) {
      navigator.vibrate(25);
    }
  }, 500);
}

function handlePressMove(event) {
  if (!pressStartPoint) return;

  const distanceX = Math.abs(event.clientX - pressStartPoint.x);
  const distanceY = Math.abs(event.clientY - pressStartPoint.y);

  if (distanceX > 8 || distanceY > 8) {
    cancelPressTimer();
  }
}

function cancelPressTimer() {
  if (pressTimer) {
    window.clearTimeout(pressTimer);
    pressTimer = null;
  }

  pressStartPoint = null;
}

function closeMessageMenu() {
  showMessageMenu.value = false;
  selectedMessage.value = null;
  cancelPressTimer();
}



async function setTyping(isTyping) {
if (!props.chatId || !props.currentUserId) return;
const db = getFirestore();
const q = query(
  collection(db, "Chats"),
  where("chat_id", "==", props.chatId)
);
const snapshot = await getDocs(q);
const batch = writeBatch(db);
snapshot.docs.forEach(docSnap => {
  batch.update(docSnap.ref, {
    typing_user_id: isTyping ? props.currentUserId : null,
    typing_updated_at: serverTimestamp()
  });
});
await batch.commit();
}

const emit = defineEmits(['chatCreated', 'ghostDetected']);
const router = useRouter()
const props = defineProps({
  chatId: {
    type: String,
    required: true,
  },
  interlocutor: {
    type: Object,
    required: true,
  },
  currentUserId: {
    type: String,
    required: true,
  },
  source: {
    type: String,
    default: null
  }
});
const interlocutorOnline = ref(props.interlocutor?.isOnline ?? false);
const messages = ref([]);
const newMessage = ref("");
const messagesList = ref(null);
const lastMessageId = ref(null);
const isLoadingMessages = ref(true);
const isGhostChat = ref(false);
const isDeleting = ref(false)
const isInterlocutorTyping = ref(false);
const loadedImages = ref({});
const downloadingFiles = ref({});
const fileInput = ref(null);

let unsubscribe = null;

watch(() => props.chatId, async (newChatId, oldChatId) => {
  if (!newChatId || newChatId === oldChatId) return;

  messages.value = [];
  lastMessageId.value = null;
  await loadMessages();
  scrollToBottom();
});

watch(
  () => props.interlocutor,
  (newInterlocutor) => {
    interlocutorOnline.value = newInterlocutor?.isOnline ?? false;
  },
  { deep: true, immediate: true }
);

watch(messages, (newMessages) => {
  console.log('Массив сообщений обновился:', newMessages);
}, { deep: true });

const observer = new IntersectionObserver(entries => {
  entries.forEach(entry => {
    const messageEl = entry.target;
    const messageId = messageEl.dataset.id;
    const isVisible = entry.isIntersecting;

    if (isVisible) {
      const msg = messages.value.find(m => m.id === messageId);

      if (msg && !msg.isRead && msg.senderId !== props.currentUserId) {
        msg.isRead = true; 
        ChatsService.markMessagesAsRead([msg.id]); 
      }
    }
  });
}, {
  threshold: 1.0
});

function observeMessage(el, message) {
  if (!el || message.senderId === props.currentUserId || message.isRead) return;
  el.dataset.id = message.id;
  observer.observe(el);
}

onMounted(async () => {

  listenTyping();

  console.log('🚀 Новый ChatWindow создан для чата:', props.chatId);
  console.log('📦 Source параметр:', props.source);

  messages.value = [];
  lastMessageId.value = null;
  isLoadingMessages.value = true; 

  await nextTick()
  await new Promise(resolve => setTimeout(resolve, 0))
  await loadMessages()

  scrollToBottom();

  unsubscribe = ChatsService.listenForNewMessages(
  props.chatId,
  handleRealtimeMessage
);
});

onUnmounted(() => {
  if (unsubscribe) unsubscribe();
  if (unsubscribeTyping) unsubscribeTyping();

  observer.disconnect();
  setTyping(false);
});

async function loadMessages() {
  try {
    const response = await ChatsService.getChatMessages(
      props.chatId,
      20,
      lastMessageId.value,
      props.interlocutor?.userId
    );

    console.log("📦 getChatMessages response:", response);

    const {
      messages: fetchedMessages,
      nextLastMessageId,
      is_online,
      isOnline
    } = response;

    interlocutorOnline.value = is_online ?? isOnline ?? false;

    messages.value = fetchedMessages.map(msg => ({
      id: msg.message_id,
      textMessage: msg.text_message || "",
      sendDate: msg.send_date,
      senderId: msg.sender_id,
      chatId: msg.chat_id,
      isRead: msg.is_read ?? false,
      is_deleted: msg.is_deleted ?? false,

      imageUrl: msg.image_url || null,

      messageType: msg.message_type || null,
      attachmentUrl: msg.attachment_url || null,
      attachmentName: msg.attachment_name || null,
      attachmentMimeType: msg.attachment_mime_type || null,
      attachmentSize: msg.attachment_size || null,
      storagePath: msg.storage_path || null,

      replyTo: normalizeReply(msg.reply_to || msg.replyTo),
      
      isSending: false,
      isFailed: false,

      videoConferenceId: msg.video_conference_id || null,
      videoProvider: msg.video_provider || null,
      videoProviderConferenceId: msg.video_provider_conference_id || null,
      videoJoinUrl: msg.video_join_url || null,
      videoTitle: msg.video_title || null,
      videoStatus: msg.video_status || null,
    })).reverse();

    lastMessageId.value = nextLastMessageId;

    isGhostChat.value = response.is_new_chat ?? messages.value.length === 0;
    emit('ghostDetected', isGhostChat.value);

    const unreadMessages = messages.value
      .filter(msg => msg.senderId !== props.currentUserId && !msg.isRead)
      .map(msg => msg.id);

    if (unreadMessages.length > 0) {
      await ChatsService.markMessagesAsRead(unreadMessages);
    }

  } catch (error) {
    console.error('❌ Ошибка загрузки сообщений:', error);
  } finally {
    isLoadingMessages.value = false;
  }
}

async function sendMessageToBackend(localMessage) {
  try {
    const chatIdToUse = props.chatId;

    const payload = {
      chat_id: chatIdToUse,
      sender_id: props.currentUserId,
      sender_name:
        props.interlocutor?.name ?? "Пользователь",

      text_message:
        localMessage.textMessage || "",

      source:
        props.source ?? "default"
    };

    const ghostMode =
      !chatIdToUse &&
      isGhostChat.value;

    if (ghostMode) {
      payload.is_ghost_chat = true;
      payload.interlocutor_id =
        props.interlocutor.userId;
    }

    if (
      localMessage.attachmentUrl &&
      localMessage.attachmentUrl !== "local-file"
    ) {
      payload.attachment_url =
        localMessage.attachmentUrl;

      payload.attachment_name =
        localMessage.attachmentName;

      payload.attachment_mime_type =
        localMessage.attachmentMimeType;

      payload.attachment_size =
        localMessage.attachmentSize;

      payload.storage_path =
        localMessage.storagePath;
    }

    if (localMessage.replyTo) {
      payload.reply_to = {
        message_id:
          localMessage.replyTo.messageId,

        sender_id:
          localMessage.replyTo.senderId,

        sender_name:
          localMessage.replyTo.senderName,

        text:
          localMessage.replyTo.text || "",

        image_url:
          localMessage.replyTo.imageUrl || null,

        attachment_name:
          localMessage.replyTo.attachmentName || null,

        message_type:
          localMessage.replyTo.messageType || "text"
      };
    }

    const response =
      await ChatsService.sendMessage(payload);

    console.log(
      "📩 sendMessage response:",
      response
    );

    const serverMessageId =
      response?.message_id ||
      response?.messageId ||
      null;

    const success =
      response?.success === true ||
      Boolean(serverMessageId);

    if (!success) {
      throw new Error(
        response?.error ||
        "Message send failed"
      );
    }

    /*
     id оставляем temp-*.
     Серверный id сохраняем отдельно.
     Realtime listener по нему найдёт
     и заменит optimistic-сообщение.
    */
    localMessage.serverId =
      serverMessageId;

    localMessage.chatId =
      response?.chat_id ||
      response?.chatId ||
      chatIdToUse;

    if (ghostMode && localMessage.chatId) {
      emit(
        "chatCreated",
        localMessage.chatId
      );
    }

    messages.value = [...messages.value];

  } catch (error) {
    console.error(
      "❌ sendMessage error:",
      error
    );

    localMessage.isSending = false;
    localMessage.isFailed = true;

    messages.value = [...messages.value];
  }
}

async function sendMessage() {
  const trimmedMessage =
    newMessage.value.trim();

  if (!trimmedMessage) {
    return;
  }

  await setTyping(false);

  isTypingSent = false;
  clearTimeout(typingTimer);

  const replySource =
    repliedMessage.value;

  const replyTo = replySource
    ? {
        messageId: replySource.id,

        senderId:
          replySource.senderId || "",

        senderName:
          replySource.senderId ===
          props.currentUserId
            ? "Вы"
            : replySource.senderName ||
              props.interlocutor?.name ||
              "Пользователь",

        text:
          replySource.textMessage || null,

        imageUrl:
          replySource.imageUrl || null,

        attachmentName:
          replySource.attachmentName || null,

        messageType:
          resolveWebMessageType(replySource)
      }
    : null;

  const temporaryId =
    typeof crypto?.randomUUID === "function"
      ? `temp-${crypto.randomUUID()}`
      : `temp-${Date.now()}-${Math.random()}`;

  const localMessage = {
    id: temporaryId,
    serverId: null,

    senderId:
      props.currentUserId,

    senderName:
      "Вы",

    textMessage:
      trimmedMessage,

    sendDate:
      new Date().toISOString(),

    isRead: false,
    isSending: true,
    isFailed: false,
    is_deleted: false,

    replyTo
  };

  newMessage.value = "";
  clearReply();

  messages.value.push(localMessage);
  messages.value = [...messages.value];

  nextTick(scrollToBottom);

  await sendMessageToBackend(
    localMessage
  );
}

function resolveWebMessageType(message) {
  if (message.imageUrl) {
    return "image";
  }

  if (
    message.attachmentUrl ||
    message.attachmentName
  ) {
    return "file";
  }

  return "text";
}

async function retryMessage(message) {
  if (!message?.isFailed || message.isSending) return;
  message.isFailed = false;
  message.isSending = true;
  message.sendDate = new Date().toISOString();
  nextTick(scrollToBottom);
  await sendMessageToBackend(message);
}

async function handleFileSelected(event) {
  const file = event.target.files?.[0];
  event.target.value = "";

  if (!file) return;

  const tempId = `temp-file-${Date.now()}`;

  const localMessage = {
    id: tempId,
    senderId: props.currentUserId,
    textMessage: newMessage.value.trim(),
    sendDate: new Date().toISOString(),
    chatId: props.chatId,
    isRead: true,
    isSending: true,
    isFailed: false,
    is_deleted: false,

    imageUrl: file.type.startsWith("image/")
      ? URL.createObjectURL(file)
      : null,

    attachmentUrl: file.type.startsWith("image/")
      ? null
      : "local-file",

    attachmentName: file.name,
    attachmentMimeType: file.type,
    attachmentSize: file.size,
    storagePath: null,

    localFile: file,
    localObjectUrl: null,
  };

  if (localMessage.imageUrl) {
    localMessage.localObjectUrl = localMessage.imageUrl;
    loadedImages.value[tempId] = true;
  }

  messages.value.push(localMessage);
  newMessage.value = "";

  nextTick(scrollToBottom);

  try {
    let chatIdToUse = props.chatId;

    if (!chatIdToUse && isGhostChat.value) {
      const ghostTextMessage = {
        ...localMessage,
        textMessage: localMessage.textMessage || "📎 Файл",
      };

      await sendMessageToBackend(ghostTextMessage);

      chatIdToUse = ghostTextMessage.chatId;
      localMessage.chatId = chatIdToUse;
    }

    if (!chatIdToUse) {
      throw new Error("Не удалось определить chat_id для загрузки файла");
    }

    const uploadResult = await ChatsService.uploadChatFile(file, chatIdToUse);

    const payload = {
      chat_id: chatIdToUse,
      sender_id: props.currentUserId,
      sender_name: props.interlocutor?.name ?? "Пользователь",
      text_message: localMessage.textMessage || "",
      source: props.source ?? "default",

      attachment_url: uploadResult.url,
      attachment_name: uploadResult.file_name,
      attachment_mime_type: uploadResult.mime_type,
      attachment_size: uploadResult.size,
      storage_path: uploadResult.storage_path,
    };

    const response = await ChatsService.sendMessage(payload);

    const success =
      response?.success === true ||
      response?.message_id ||
      response?.messageId;

    if (!success) {
      throw new Error(response?.error || "Message send failed");
    }

    const oldTempId = localMessage.id;
    const serverMessageId =
      response.message_id ||
      response.messageId ||
      localMessage.id;

    localMessage.chatId =
      response.chat_id ||
      response.chatId ||
      chatIdToUse;

    localMessage.isSending = false;
    localMessage.isFailed = false;

    const isImage = uploadResult.mime_type?.startsWith("image/");

    localMessage.imageUrl = isImage
      ? uploadResult.url
      : null;

    localMessage.attachmentUrl = uploadResult.url;
    localMessage.attachmentName = uploadResult.file_name;
    localMessage.attachmentMimeType = uploadResult.mime_type;
    localMessage.attachmentSize = uploadResult.size;
    localMessage.storagePath = uploadResult.storage_path;

    localMessage.id = serverMessageId;

    if (isImage) {
      loadedImages.value[serverMessageId] = true;
      loadedImages.value[oldTempId] = true;
    }
    localMessage.attachmentName = uploadResult.file_name;
    localMessage.attachmentMimeType = uploadResult.mime_type;
    localMessage.attachmentSize = uploadResult.size;
    localMessage.storagePath = uploadResult.storage_path;

    if (localMessage.localObjectUrl) {
      URL.revokeObjectURL(localMessage.localObjectUrl);
      localMessage.localObjectUrl = null;
    }

    const index = messages.value.findIndex(m =>
      m.id === oldTempId ||
      m.id === serverMessageId
    );

    if (index !== -1) {
      messages.value[index] = {
        ...messages.value[index],
        ...localMessage
      };
    }

    messages.value = [...messages.value];

    nextTick(scrollToBottom);

  } catch (error) {
    console.error("❌ upload/send file error:", error);

    localMessage.isSending = false;
    localMessage.isFailed = true;

    messages.value = [...messages.value];
  }
}

function scrollToBottom() {
  if (messagesList.value) {
    messagesList.value.scrollTop = messagesList.value.scrollHeight;
  }
}

function formatTime(dateString) {
  if (!dateString) return "";

  const cleanDateString = dateString.split(".")[0];
  const date = new Date(cleanDateString);

  if (isNaN(date)) {
    return "";
  }

  return date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
}

function handleTyping() {
  const hasText = newMessage.value.trim().length > 0;

  if (hasText && !isTypingSent) {
    setTyping(true);
    isTypingSent = true;
  }

  clearTimeout(typingTimer);

  typingTimer = setTimeout(() => {
    setTyping(false);
    isTypingSent = false;
  }, 1500);

  if (!hasText) {
    setTyping(false);
    isTypingSent = false;
  }
}

function listenTyping() {
  if (!props.chatId || !props.interlocutor?.userId) return;

  const db = getFirestore();

  const q = query(
    collection(db, "Chats"),
    where("chat_id", "==", props.chatId),
    where("owner_id", "==", props.currentUserId)
  );

  unsubscribeTyping = onSnapshot(q, snapshot => {
    const data = snapshot.docs[0]?.data();
    if (!data) return;

    isInterlocutorTyping.value =
      data.typing_user_id === props.interlocutor.userId;
  });
}

async function deleteMessage() {
  if (isDeleting.value) return;
  isDeleting.value = true;

  try {
    const msgId = selectedMessage.value?.id;
    if (!msgId) return;

    let result;

    try {
      result = await ChatsService.deleteMessage(msgId, false);
    } catch (error) {
      console.warn("Мягкое удаление не удалось, пробуем force-удаление", error);
      result = await ChatsService.deleteMessage(msgId, true);
    }

    if (result.success) {
      const msg = messages.value.find(m => m.id === msgId);
      if (msg) {
        msg.is_deleted = true;
      }

      showDeleteMenu.value = false;
      selectedMessage.value = null;
      console.log(`🗑 Сообщение ${msgId} удалено (force: ${result.force || false})`);
    }

  } catch (error) {
    console.error("Ошибка при удалении сообщения:", error);
  } finally {
    isDeleting.value = false;
  }
}
async function downloadFile(message) {
  if (!message?.attachmentUrl) return;
  if (downloadingFiles.value[message.id]) return;

  downloadingFiles.value[message.id] = true;

  try {
    const response = await fetch(message.attachmentUrl);

    if (!response.ok) {
      throw new Error("File download failed");
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = message.attachmentName || "file";
    document.body.appendChild(link);
    link.click();

    showDeleteMenu.value = false;
    showMessageMenu.value = false;
    selectedMessage.value = null;

    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("❌ file download error:", error);
    window.open(message.attachmentUrl, "_blank");
  } finally {
    downloadingFiles.value[message.id] = false;
  }
}

function fileIcon(message) {
  const ext = message.attachmentName
    ? message.attachmentName.split(".").pop()?.toLowerCase()
    : "";

  switch (ext) {
    case "pdf":
      return "📕";
    case "doc":
    case "docx":
      return "📘";
    case "xls":
    case "xlsx":
      return "📗";
    case "zip":
    case "rar":
    case "7z":
      return "📦";
    case "json":
      return "{}";
    case "txt":
      return "📄";
    default:
      return "📎";
  }
}

function openFilePicker() {
  fileInput.value?.click();
}

  function openFile(url) {
    if (!url) return;
    window.open(url, "_blank");
  }

  function openImage(url) {
    if (!url) return;
    window.open(url, "_blank");
  }

  function fileMeta(message) {
    const ext = message.attachmentName
      ? message.attachmentName.split(".").pop()?.toUpperCase()
      : null;

    const size = formatFileSize(message.attachmentSize);

    if (ext && size) return `${ext} • ${size}`;
    if (ext) return ext;
    if (size) return size;

    return "Файл";
  }

  function formatFileSize(bytes) {
    if (!bytes) return "";

    const units = ["B", "KB", "MB", "GB"];
    let size = Number(bytes);
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size = size / 1024;
      unitIndex++;
    }

    return `${size.toFixed(size >= 10 ? 0 : 1)} ${units[unitIndex]}`;
  }


  async function createVideoConference() {
  if (isCreatingConference.value) return;

  const chatIdToUse = props.chatId;

  if (!chatIdToUse) {
    console.warn("⚠️ Нельзя создать видеозвонок без chat_id");
    return;
  }

  isCreatingConference.value = true;

  try {
    const result = await VideoConferenceService.createConference(
      chatIdToUse,
      `Видеозвонок с ${props.interlocutor?.name || "пользователем"}`
    );

    if (!result?.success) {
      throw new Error(
        result?.error || "Не удалось создать видеоконференцию"
      );
    }

    console.log("✅ Видеоконференция создана:", result);

    await nextTick();
    scrollToBottom();

  } catch (error) {
    console.error(
      "❌ Ошибка создания видеоконференции:",
      error?.message || error
    );
  } finally {
    isCreatingConference.value = false;
  }
}
  
function openVideoConference(url) {
if (!url) return;
window.open(
  url,
  "_blank",
  "noopener,noreferrer"
);
}

function openMessageContextMenu(message, event) {
  cancelPressTimer();

  selectedMessage.value = message;

  const menuWidth = 220;
  const menuHeight = 150;
  const padding = 12;

  messageMenuPosition.value = {
    left: Math.min(
      Math.max(event.clientX, padding),
      window.innerWidth - menuWidth - padding
    ),
    top: Math.min(
      Math.max(event.clientY, padding),
      window.innerHeight - menuHeight - padding
    )
  };

  showMessageMenu.value = true;
}

const canReplyToSelectedMessage = computed(() => {
  const message = selectedMessage.value;

  if (!message || message.is_deleted || !message.id) {
    return false;
  }

  return Boolean(
    message.textMessage?.trim() ||
    message.imageUrl ||
    message.attachmentName ||
    message.attachmentUrl
  );
});

const canCopySelectedMessage = computed(() => {
  const message = selectedMessage.value;

  return Boolean(
    message &&
    !message.is_deleted &&
    message.textMessage?.trim()
  );
});


function replyToSelectedMessage() {
  if (!canReplyToSelectedMessage.value) return;

  repliedMessage.value = selectedMessage.value;
  closeMessageMenu();

  nextTick(() => {
    document.querySelector(".message-field")?.focus();
  });
}

function clearReply() {
  repliedMessage.value = null;
}

async function copySelectedMessage() {
  const text = selectedMessage.value?.textMessage?.trim();

  if (!text) {
    closeMessageMenu();
    return;
  }

  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";

    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    textarea.remove();
  }

  closeMessageMenu();
}

function openDeleteConfirmation() {
  showMessageMenu.value = false;
  showDeleteMenu.value = true;
}

function replyAuthorName(reply) {
  if (!reply) return "";

  if (reply.senderId === props.currentUserId) {
    return "Вы";
  }

  return reply.senderName ||
    props.interlocutor?.name ||
    "Пользователь";
}

function replyPreviewText(reply) {
  if (!reply) return "Сообщение";

  if (reply.text?.trim()) {
    return reply.text.trim();
  }

  if (reply.imageUrl) {
    return "📷 Фото";
  }

  if (reply.attachmentName) {
    return `📎 ${reply.attachmentName}`;
  }

  switch (reply.messageType) {
    case "image":
      return "📷 Фото";

    case "file":
      return "📎 Файл";

    default:
      return "Сообщение";
  }
}

function messagePreviewText(message) {
  if (!message) return "Сообщение";

  if (message.textMessage?.trim()) {
    return message.textMessage.trim();
  }

  if (message.imageUrl) {
    return "📷 Фото";
  }

  if (message.attachmentName) {
    return `📎 ${message.attachmentName}`;
  }

  if (message.attachmentUrl) {
    return "📎 Файл";
  }

  return "Сообщение";
}

function scrollToRepliedMessage(messageId) {
  if (!messageId) return;

  const escapedId =
    window.CSS?.escape
      ? window.CSS.escape(messageId)
      : messageId.replace(/["\\]/g, "\\$&");

  const element = document.querySelector(
    `[data-message-id="${escapedId}"]`
  );

  if (!element) return;

  element.scrollIntoView({
    behavior: "smooth",
    block: "center"
  });

  element.classList.remove("reply-target-highlight");

  requestAnimationFrame(() => {
    element.classList.add("reply-target-highlight");

    window.setTimeout(() => {
      element.classList.remove("reply-target-highlight");
    }, 1400);
  });
}

function closeDeleteConfirmation() {
  showDeleteMenu.value = false;
  selectedMessage.value = null;
}

function normalizeReply(reply) {
  if (!reply) return null;

  return {
    messageId:
      reply.messageId ||
      reply.message_id ||
      "",

    senderId:
      reply.senderId ||
      reply.sender_id ||
      "",

    senderName:
      reply.senderName ||
      reply.sender_name ||
      "Пользователь",

    text:
      reply.text ??
      reply.text_message ??
      null,

    imageUrl:
      reply.imageUrl ||
      reply.image_url ||
      null,

    attachmentName:
      reply.attachmentName ||
      reply.attachment_name ||
      null,

    messageType:
      reply.messageType ||
      reply.message_type ||
      "text"
  };
}

function normalizeIncomingMessage(rawMessage) {
  return {
    id:
      rawMessage.id ||
      rawMessage.messageId ||
      rawMessage.message_id,

    textMessage:
      rawMessage.textMessage ??
      rawMessage.text_message ??
      "",

    sendDate:
      typeof rawMessage.sendDate === "object" &&
      typeof rawMessage.sendDate?.toDate === "function"
        ? rawMessage.sendDate.toDate().toISOString()
        : rawMessage.sendDate ||
          rawMessage.send_date ||
          new Date().toISOString(),

    senderId:
      rawMessage.senderId ||
      rawMessage.sender_id ||
      "",

    senderName:
      rawMessage.senderName ||
      rawMessage.sender_name ||
      "",

    chatId:
      rawMessage.chatId ||
      rawMessage.chat_id ||
      null,

    isRead:
      rawMessage.isRead ??
      rawMessage.wasRead ??
      rawMessage.is_read ??
      rawMessage.was_read ??
      false,

    is_deleted:
      rawMessage.is_deleted ??
      rawMessage.isDeleted ??
      false,

    imageUrl:
      rawMessage.imageUrl ||
      rawMessage.image_url ||
      null,

    messageType:
      rawMessage.messageType ||
      rawMessage.message_type ||
      null,

    attachmentUrl:
      rawMessage.attachmentUrl ||
      rawMessage.attachment_url ||
      null,

    attachmentName:
      rawMessage.attachmentName ||
      rawMessage.attachment_name ||
      null,

    attachmentMimeType:
      rawMessage.attachmentMimeType ||
      rawMessage.attachment_mime_type ||
      null,

    attachmentSize:
      rawMessage.attachmentSize ||
      rawMessage.attachment_size ||
      null,

    storagePath:
      rawMessage.storagePath ||
      rawMessage.storage_path ||
      null,

    videoConferenceId:
      rawMessage.videoConferenceId ||
      rawMessage.video_conference_id ||
      null,

    videoProvider:
      rawMessage.videoProvider ||
      rawMessage.video_provider ||
      null,

    videoProviderConferenceId:
      rawMessage.videoProviderConferenceId ||
      rawMessage.video_provider_conference_id ||
      null,

    videoJoinUrl:
      rawMessage.videoJoinUrl ||
      rawMessage.video_join_url ||
      null,

    videoTitle:
      rawMessage.videoTitle ||
      rawMessage.video_title ||
      null,

    videoStatus:
      rawMessage.videoStatus ||
      rawMessage.video_status ||
      null,

    replyTo: normalizeReply(
      rawMessage.replyTo ||
      rawMessage.reply_to
    ),

    isSending: false,
    isFailed: false
  };
}

function replyIdsMatch(localMessage, serverMessage) {
  const localReplyId =
    localMessage.replyTo?.messageId || null;

  const serverReplyId =
    serverMessage.replyTo?.messageId || null;

  // Оба отсутствуют — обычные сообщения.
  if (!localReplyId && !serverReplyId) {
    return true;
  }

  // Firestore mapper мог пока не передать reply_to.
  // Разрешаем fallback по тексту и времени.
  if (localReplyId && !serverReplyId) {
    return true;
  }

  if (!localReplyId && serverReplyId) {
    return false;
  }

  return localReplyId === serverReplyId;
}

function findOptimisticMessageIndex(serverMessage) {
  // Самый надёжный вариант:
  // backend уже вернул message_id и мы записали его в serverId.
  const exactServerIdIndex = messages.value.findIndex(message => {
    return (
      message.id?.startsWith?.("temp-") &&
      message.serverId &&
      message.serverId === serverMessage.id
    );
  });

  if (exactServerIdIndex !== -1) {
    return exactServerIdIndex;
  }

  // Fallback, если realtime пришёл раньше HTTP-ответа.
  return messages.value.findIndex(localMessage => {
    const isTemporary =
      typeof localMessage.id === "string" &&
      localMessage.id.startsWith("temp-");

    if (!isTemporary) return false;
    if (localMessage.isFailed) return false;

    if (localMessage.senderId !== serverMessage.senderId) {
      return false;
    }

    const localText =
      localMessage.textMessage?.trim() || "";

    const serverText =
      serverMessage.textMessage?.trim() || "";

    if (localText !== serverText) {
      return false;
    }

    if (!replyIdsMatch(localMessage, serverMessage)) {
      return false;
    }

    const localTime =
      new Date(localMessage.sendDate).getTime();

    const serverTime =
      new Date(serverMessage.sendDate).getTime();

    if (
      Number.isFinite(localTime) &&
      Number.isFinite(serverTime) &&
      Math.abs(localTime - serverTime) > 30_000
    ) {
      return false;
    }

    const localHasAttachment = Boolean(
      localMessage.attachmentName ||
      localMessage.attachmentUrl ||
      localMessage.imageUrl ||
      localMessage.localFile
    );

    const serverHasAttachment = Boolean(
      serverMessage.attachmentName ||
      serverMessage.attachmentUrl ||
      serverMessage.imageUrl
    );

    if (localHasAttachment || serverHasAttachment) {
      const sameName =
        localMessage.attachmentName &&
        serverMessage.attachmentName &&
        localMessage.attachmentName ===
          serverMessage.attachmentName;

      return Boolean(sameName);
    }

    return true;
  });
}

function handleRealtimeMessage(rawMessage) {
  const serverMessage =
    normalizeIncomingMessage(rawMessage);

  if (!serverMessage.id) {
    console.warn(
      "⚠️ Realtime-сообщение пришло без id:",
      rawMessage
    );
    return;
  }

  // Сначала ищем уже существующее серверное сообщение.
  const existingIndex = messages.value.findIndex(
    message => message.id === serverMessage.id
  );

  if (existingIndex !== -1) {
    messages.value[existingIndex] = {
      ...messages.value[existingIndex],
      ...serverMessage,

      // Не теряем replyTo, если realtime mapper
      // временно его не передал.
      replyTo:
        serverMessage.replyTo ||
        messages.value[existingIndex].replyTo ||
        null
    };

    messages.value = [...messages.value];
    return;
  }

  // Ищем optimistic temp-сообщение.
  const optimisticIndex =
    findOptimisticMessageIndex(serverMessage);

  if (optimisticIndex !== -1) {
    const optimisticMessage =
      messages.value[optimisticIndex];

    const oldTempId = optimisticMessage.id;

    if (serverMessage.imageUrl) {
      loadedImages.value[serverMessage.id] =
        loadedImages.value[oldTempId] ?? true;
    }

    messages.value[optimisticIndex] = {
      ...optimisticMessage,
      ...serverMessage,

      id: serverMessage.id,

      replyTo:
        serverMessage.replyTo ||
        optimisticMessage.replyTo ||
        null,

      isSending: false,
      isFailed: false,
      serverId: undefined
    };

    messages.value = [...messages.value];

    nextTick(scrollToBottom);
    return;
  }

  // Это новое сообщение другого пользователя
  // либо сообщение, для которого не было optimistic-версии.
  messages.value.push(serverMessage);
  messages.value = [...messages.value];

  nextTick(scrollToBottom);
}

</script>
  
<style scoped>

.chat-window {
  position: relative;
  display: flex; 
  flex-direction: column;
  height: 100%;
  width: 600px;
  background: var(--chat-bg);
}

.chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-bottom: 1px solid var(--border-color);
  background: var(--sidebar-background);
}

.left-part {
  display: flex;
  align-items: center;
}

.avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 12px;
}

.info {
  display: flex;
  flex-direction: column;
}

.name {
  font-size: 16px;
  font-family: "Eastman-Bold", sans-serif;
  color: var(--text-primary);
  margin: 0;
}

.specifics {
  font-size: 12px;
  font-family: "Eastman-Regular", sans-serif;
  color: var(--text-secondary);
  margin: 2px 0 0 0;
}

.messages-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  background: var(--background-color);
  display: flex;
  flex-direction: column;
  gap: 10px;
  scrollbar-width: none;
}

.message-item {
  display: flex;
  align-items: flex-start;
  padding: 8px 12px;
  background: lightblue;
  border-radius: 12px;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  color: var(--text-primary);
  position: relative;
  word-wrap: break-word;
  width: auto;
  margin-left: 40px;
}

.own-message {
  background: #2173e5;
  color: white;
  align-self: flex-end;
  margin-left: auto;
}

.avatar-container {
  margin-right: 15px; 
  margin-left: -60px; 
}

.message-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  margin-left: 5px;
}

.message-content {
  display: flex;
  flex-direction: column;
}

.text {
  margin: 0;
  color: var(--text-primary);
}

.time {
  display: block;
  font-size: 10px;
  color: var(--text-secondary);
  margin-top: 4px;
  text-align: right;
}

.message-input {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-top: 1px solid var(--border-color);
  background: var(--sidebar-background);
  gap: 10px;
}

.message-field {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #e0e3ea;
  border-radius: 18px;
  outline: none;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  background-color: transparent;
  color: var(--text-primary);
  border: 1px solid var(--chat-input-border);
}

.message-field::placeholder {
 color: var(--text-secondary);
}

.file-button,
.send-button {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-button img,
.send-button img {
  width: 20px;
  height: 20px;
  opacity: var(--icon-opacity);
}

.send-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.chat-separator {
  height: 1px;
  width: 100%;
 background-color: var(--separator_home);
}

.right-separator {
  position: absolute;
  top: 0;
  right: 0;
  width: 1px;
  height: 100%;
  background-color: var(--sidebar-separator);
  z-index: 2;
}

.status-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.status-dot.online {
  background-color: #34c759;
}

.status-dot.offline {
  background-color: #c4c4c4;
}

.status-text {
  font-size: 12px;
  color: gray;
  font-family: "Eastman-Regular", sans-serif;
}

.call-button {
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: var(--chat-call-bg);
  padding: 8px 14px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  font-family: "Eastman-Medium", sans-serif;
  color: var(--chat-call-text);
  font-size: 14px;
}

.call-button:hover {
  background-color: var(--chat-call-hover-bg);
}

.call-button img {
  width: 16px;
  height: 16px;
}

.read-icon {
  width: 14px;
  height: 14px;
  margin-left: 6px;
  vertical-align: middle;
  opacity: 0.7;
}

.message-skeleton-wrapper {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
}

.message-skeleton-wrapper.own {
  justify-content: flex-end;
}

.message-skeleton-wrapper.interlocutor {
  justify-content: flex-start;
}

.skeleton-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  margin-right: 12px;
  flex-shrink: 0;
  background-color: #ebebeb;
}

.message-skeleton-bubble {
  padding: 12px 16px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 70%;
  background-color: #ebebeb;
}

.skeleton-line {
  height: 12px;
  border-radius: 6px;
  margin-bottom: 6px;
  background-color: #ebebeb;
}

.skeleton-line.short {
  width: 50%;
}

.shimmer {
  background: linear-gradient(
    to right,
    #e4e4e4 0%,
    #e8e8e8 20%,
    #e5e5e5 40%
  );
  background-size: 1000px 100%;
  animation: shimmer 11.5s infinite linear;
}

@keyframes shimmer {
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
}

:root[data-theme="dark"] .skeleton-avatar,
:root[data-theme="dark"] .message-skeleton-bubble,
:root[data-theme="dark"] .skeleton-line {
  background-color: #333333;
}

:root[data-theme="dark"] .shimmer {
  background: linear-gradient(
    to right,
    #2a2a2a 0%,
    #3a3a3a 20%,
    #2a2a2a 40%
  );
}

.delete-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.delete-popup {
  background: var(--sidebar-background);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.2);
  text-align: center;
  min-width: 240px;
}

.delete-popup p {
  font-size: 14px;
  margin-bottom: 16px;
  color: var(--text-primary);
  font-family: "Eastman-Regular", sans-serif;
}

.delete-button,
.cancel-button {
  padding: 8px 14px;
  font-size: 14px;
  border-radius: 8px;
  font-family: "Eastman-Medium", sans-serif;
  border: none;
  cursor: pointer;
  margin: 0 4px;
}

.delete-button {
  background-color: #ff3b30;
  color: white;
}

.cancel-button {
  background-color: #e0e0e0;
  color: #333;
}

.text.deleted {
  font-family: sans-serif;
  font-style: italic;
  font-weight: 300;
  font-size: 15px;
  color: var(--text-secondary);
  opacity: 0.65;
}

.call-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.empty-chat-placeholder {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  text-align: center;
  margin-top: 80px;
}

.empty-chat-icon {
  width: 96px;
  height: auto;
  margin-bottom: 12px;
}

.empty-chat-text {
  font-size: 16px;
  font-family: "Eastman-Medium", sans-serif;
  margin: 0;
}

.empty-chat-subtext {
  font-size: 14px;
  font-family: "Eastman-Regular", sans-serif;
  margin-top: 6px;
  color: var(--text-secondary);
}

@media (max-width: 430px) {

  .chat-window {
    width: 100%;
    height: 100vh;
  }

  .message-item {
    max-width: 82%;
    padding: 10px 14px;
    border-radius: 18px;
    font-size: 14px;
    }
}

.message-item {
  animation: messagePopIn 0.28s ease forwards;
}

.sending-message {
  opacity: 0.62;
  transform: translateY(4px) scale(0.98);
}

.sending-message .message-content {
  position: relative;
  padding-right: 18px;
}

.sending-message .message-content::after {
  content: "";
  position: absolute;
  right: 0;
  bottom: 2px;
  width: 9px;
  height: 9px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: white;
  border-radius: 50%;
  animation: spinSending 0.7s linear infinite;
}

.sending-message:not(.own-message) .message-content::after {
  border-color: rgba(33, 115, 229, 0.25);
  border-top-color: #2173e5;
}

.failed-message {
  background: #ff4d4f !important;
}

.sending-label,
.failed-label {
  margin-left: 6px;
  font-size: 10px;
  opacity: 0.75;
}

.failed-label {
  color: #ffd1d1;
}

@keyframes messagePopIn {
  0% {
    opacity: 0;
    transform: translateY(12px) scale(0.96);
  }

  70% {
    opacity: 1;
    transform: translateY(-1px) scale(1.01);
  }

  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes spinSending {
  to {
    transform: rotate(360deg);
  }
}

.failed-message {
  background: rgba(255, 59, 48, 0.9) !important;
}

.failed-label {
  margin-left: 6px;
  font-size: 10px;
  color: #ffe0e0;
  font-family: "Eastman-Regular", sans-serif;
}

.retry-button {
  margin-left: 8px;
  padding: 2px 7px;
  border: none;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
  font-size: 10px;
  font-family: "Eastman-Medium", sans-serif;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease;
}

.retry-button:hover {
  background: rgba(255, 255, 255, 0.32);
  transform: translateY(-1px);
}

.retry-button:active {
  transform: scale(0.96);
}

.attach-stub {
  position: absolute;
  left: 50%;
  bottom: 82px;
  transform: translateX(-50%);
  z-index: 20;

  display: flex;
  align-items: center;
  gap: 8px;

  padding: 11px 16px;
  border-radius: 999px;

  background: var(--modal-bg, #ffffff);
  color: var(--text-primary);

  font-size: 12px;
  font-family: "Eastman-Medium", sans-serif;

  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.16);
  border: 1px solid rgba(0, 0, 0, 0.06);

  white-space: nowrap;
  pointer-events: none;
}

.attach-stub img {
  width: 16px;
  height: 16px;
  opacity: 0.75;
}

html[data-theme="dark"] .attach-stub {
  background: #2a2a2a;
  border-color: #3a3a3a;
  box-shadow: 0 14px 38px rgba(0, 0, 0, 0.45);
}

.attach-toast-enter-active,
.attach-toast-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.attach-toast-enter-from,
.attach-toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(8px) scale(0.96);
}

@media (max-width: 430px) {
  .attach-stub {
    bottom: 88px;
    max-width: calc(100% - 32px);
    white-space: normal;
    text-align: center;
    border-radius: 18px;
  }
}

html[data-theme="dark"] .message-item:not(.own-message) .read-icon {
filter: brightness(0) invert(1);
opacity: 0.75;
}

.read-blue {
filter: brightness(0) saturate(100%)
        invert(48%) sepia(96%)
        saturate(1800%)
        hue-rotate(190deg)
        brightness(105%)
        contrast(100%);
}

.status-text.typing {
  color: #2173e5;
}

.messages-list {
  gap: 8px;
}

.message-item {
  max-width: 72%;
  width: fit-content;
  padding: 8px 12px;
  box-sizing: border-box;
}

.own-message {
  max-width: 66%;
  align-self: flex-end;
  margin-left: auto;
}

.message-item:not(.own-message) {
  background: var(--chat-incoming-bg);
  max-width: 66%;
  align-self: flex-start;
  margin-right: auto;
}

.message-content {
  max-width: 100%;
  min-width: 0;
}

.text {
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.35;
}

.file-message {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 260px;
  padding: 12px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.22);
  cursor: pointer;
}

.message-item:not(.own-message) .file-message {
  background: rgba(0, 0, 0, 0.04);
}

.file-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: rgba(33, 115, 229, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
}

.file-info {
  min-width: 0;
  flex: 1;
}

.file-name {
  font-family: "Eastman-Medium", sans-serif;
  font-size: 13px;
  color: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-meta {
  margin-top: 4px;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
  opacity: 0.7;
}

.own-message .file-meta {
  color: rgba(255, 255, 255, 0.8);
}

.message-item:not(.own-message) .file-meta {
  color: var(--text-secondary);
}

.message-image-wrapper {
  position: relative;
  width: min(260px, 70vw);
  height: auto;
  border-radius: 14px;
  overflow: hidden;
  background: #e8e8e8;
  aspect-ratio: 4 / 3;
}

.message-image {
  width: 100%;
  height: 100%;
  max-width: none;
  max-height: none;
  object-fit: cover;
  object-position: center;
  display: block;
  opacity: 0;
  transition: opacity 0.25s ease;
  cursor: pointer;
}

.message-image.loaded {
  opacity: 1;
}

.image-shimmer {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(
    90deg,
    #e8e8e8 0%,
    #f3f3f3 45%,
    #e8e8e8 100%
  );
  background-size: 200% 100%;
  animation: imageShimmer 1.2s infinite linear;
}

@keyframes imageShimmer {
  from {
    background-position: 200% 0;
  }
  to {
    background-position: -200% 0;
  }
}

html[data-theme="dark"] .message-image-wrapper {
  background: #2f2f2f;
}

html[data-theme="dark"] .image-shimmer {
  background: linear-gradient(
    90deg,
    #2f2f2f 0%,
    #3a3a3a 45%,
    #2f2f2f 100%
  );
  background-size: 200% 100%;
}

.file-download {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Eastman-Medium", sans-serif;
  font-size: 16px;
  flex-shrink: 0;
}

.message-item:not(.own-message) .file-download {
  background: rgba(0, 0, 0, 0.06);
}

.file-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: white;
  border-radius: 50%;
  animation: fileSpin 0.7s linear infinite;
}

.message-item:not(.own-message) .file-spinner {
  border-color: rgba(0, 0, 0, 0.15);
  border-top-color: #2173e5;
}

@keyframes fileSpin {
  to {
    transform: rotate(360deg);
  }
}

.file-button:disabled {
  cursor: not-allowed;
  opacity: 0.8;
}

.attach-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(33, 115, 229, 0.25);
  border-top-color: #2173e5;
  border-radius: 50%;
  animation: attachSpin 0.7s linear infinite;
}

@keyframes attachSpin {
  to {
    transform: rotate(360deg);
  }
}

.chat-window {
  position: relative;
  overflow: hidden;
}

.chat-window::before,
.chat-window::after {
  content: "";
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;
  filter: blur(80px);
}

.chat-window::before {
  width: 320px;
  height: 320px;
  left: -120px;
  top: 180px;
  background: rgba(33, 115, 229, 0.12);
}

.chat-window::after {
  width: 280px;
  height: 280px;
  right: -100px;
  bottom: 120px;
  background: rgba(33, 115, 229, 0.10);
}

.video-call-message {
  width: 280px;
  padding: 14px;
  border-radius: 16px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: rgba(255, 255, 255, 0.18);
}

.message-item:not(.own-message) .video-call-message {
  background: rgba(33, 115, 229, 0.08);
}

.video-call-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  font-size: 20px;
  background: rgba(33, 115, 229, 0.14);
}

.video-call-info {
  min-width: 0;
  flex: 1;
}

.video-call-title {
  font-family: "Eastman-Medium", sans-serif;
  font-size: 13px;
  line-height: 1.3;
}

.video-call-provider {
  margin-top: 4px;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
  opacity: 0.7;
}

.video-call-open-button {
  margin-top: 10px;
  width: 100%;
  min-height: 34px;

  border: none;
  border-radius: 10px;

  background: #2173e5;
  color: white;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 12px;
  cursor: pointer;
}

.video-call-open-button:hover {
  opacity: 0.9;
}

.video-call-open-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.call-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(33, 115, 229, 0.25);
  border-top-color: #2173e5;
  border-radius: 50%;
  animation: callSpin 0.7s linear infinite;
}

@keyframes callSpin {
  to {
    transform: rotate(360deg);
  }
}

html[data-theme="dark"] .message-item:not(.own-message) .video-call-message {
  background: rgba(255, 255, 255, 0.06);
}

.reply-quote {
  position: relative;
  display: flex;
  min-width: 0;
  margin-bottom: 8px;
  padding: 8px 10px;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.17);
  transition:
    transform 0.16s ease,
    background-color 0.16s ease;
}

.message-item:not(.own-message) .reply-quote {
  background: rgba(33, 115, 229, 0.09);
}

.reply-quote:hover {
  transform: translateY(-1px);
}

.reply-accent {
  width: 3px;
  min-width: 3px;
  margin-right: 9px;
  border-radius: 999px;
  background: #75adff;
}

.message-item:not(.own-message) .reply-accent {
  background: #2173e5;
}

.reply-quote-content {
  min-width: 0;
  overflow: hidden;
}

.reply-author {
  color: inherit;
  font-family: "Eastman-Medium", sans-serif;
  font-size: 11px;
  line-height: 1.25;
}

.reply-text {
  margin-top: 2px;
  max-width: 230px;
  overflow: hidden;
  color: inherit;
  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: 0.72;
}

.reply-input-preview {
  position: relative;
  z-index: 5;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  min-height: 52px;
  padding: 8px 16px;
  border-top: 1px solid var(--border-color);
  background: var(--sidebar-background);
}

.reply-input-accent {
  width: 3px;
  height: 36px;
  flex-shrink: 0;
  margin-right: 10px;
  border-radius: 999px;
  background: #2173e5;
}

.reply-input-content {
  min-width: 0;
  flex: 1;
}

.reply-input-author {
  color: #2173e5;
  font-family: "Eastman-Medium", sans-serif;
  font-size: 12px;
}

.reply-input-text {
  margin-top: 3px;
  overflow: hidden;
  color: var(--text-secondary);
  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.reply-input-close {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  margin-left: 10px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--text-secondary);
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
}

.reply-input-close:hover {
  background: rgba(127, 127, 127, 0.12);
}

.message-menu-overlay {
  position: fixed;
  z-index: 10000;
  inset: 0;
  background: rgba(10, 16, 26, 0.18);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.message-context-menu {
  position: fixed;
  display: flex;
  width: 220px;
  flex-direction: column;
  overflow: hidden;
  padding: 6px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.86);
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.24),
    0 4px 16px rgba(0, 0, 0, 0.12);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
}

html[data-theme="dark"] .message-context-menu {
  border-color: rgba(255, 255, 255, 0.08);
  background: rgba(36, 38, 43, 0.9);
}

.message-menu-action {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 11px;
  padding: 11px 12px;
  border: none;
  border-radius: 11px;
  background: transparent;
  color: var(--text-primary);
  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
}

.message-menu-action:hover {
  background: rgba(33, 115, 229, 0.1);
}

.message-menu-action.destructive {
  color: #ff3b30;
}

.message-menu-action.destructive:hover {
  background: rgba(255, 59, 48, 0.1);
}

.message-menu-icon {
  display: inline-flex;
  width: 22px;
  align-items: center;
  justify-content: center;
  font-size: 17px;
}

.reply-target-highlight {
  animation: replyTargetHighlight 1.4s ease;
}

@keyframes replyTargetHighlight {
  0%,
  100% {
    filter: none;
    transform: scale(1);
  }

  35% {
    filter: brightness(1.12);
    transform: scale(1.025);
    box-shadow:
      0 0 0 3px rgba(33, 115, 229, 0.25),
      0 12px 28px rgba(33, 115, 229, 0.2);
  }
}

.message-menu-enter-active,
.message-menu-leave-active {
  transition: opacity 0.16s ease;
}

.message-menu-enter-active .message-context-menu,
.message-menu-leave-active .message-context-menu {
  transition:
    opacity 0.16s ease,
    transform 0.16s ease;
}

.message-menu-enter-from,
.message-menu-leave-to {
  opacity: 0;
}

.message-menu-enter-from .message-context-menu,
.message-menu-leave-to .message-context-menu {
  opacity: 0;
  transform: scale(0.94) translateY(5px);
}

.reply-preview-enter-active,
.reply-preview-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.reply-preview-enter-from,
.reply-preview-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

@media (max-width: 430px) {
  .message-context-menu {
    width: min(220px, calc(100vw - 24px));
  }

  .reply-text {
    max-width: 190px;
  }
}
</style>